import { getUserProfile } from '@/api/backend/profile';
import {
  captureGupLanding,
  getPortalRedirect,
  getPendingGupLanding,
  getGupVisit,
  returnToGup,
  clearGupVisit,
} from '@/lib/gup';
import { Button } from '@opaz/ui-components';
import { isAxiosError } from 'axios';
import { registerAccount } from '@/api/backend/account';
import { getCountries } from '@/api/backend/reference';
import { AuthForm } from '@/components/auth-form';
import { COUNTRY_MOBILE_CODES } from '@/lib/constants';
import { AuthService } from '@/services/authService';
import { FlagCountryCode, useToast } from '@opaz/ui-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SigningInCover } from '../login/-components/SigningInCover';
import { EmailPasswordForm } from './-components/EmailPasswordForm';
import { PersonalDetailsForm } from './-components/PersonalDetailsForm';
import { RegistrationStepper } from './-components/RegistrationStepper';
import { ResidencyStatusForm } from './-components/ResidencyStatusForm';
import { VerifyEmail } from './-components/VerifyEmail';

export const Route = createFileRoute('/signup/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      txId: search.txId as string | undefined,
      mpkId: search.mpkId as boolean | undefined,
      redirect: search.redirect as string | undefined,
    };
  },
  beforeLoad: async ({ search }) => {
    captureGupLanding(search.redirect);
    if (!AuthService.isTokenExpired() && getPendingGupLanding()) {
      try {
        const profile = await getUserProfile(true);
        if (profile.profiles?.length)
          throw redirect({ to: getPortalRedirect(search.redirect) || getPendingGupLanding()! });
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 404) throw error;
      }
      return;
    }
    if (AuthService.isTokenExpired()) {
      console.debug('[/signup]: User is not authenticated.');
    } else {
      console.debug('[/signup]: User is authenticated. Redirecting...');
      throw redirect({ to: getPortalRedirect(search.redirect) || '/dashboard' });
    }
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Signup - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { txId, mpkId, redirect: redirectUrl } = Route.useSearch();
  const { toast } = useToast();

  // TODO: Remove the isPkiLoginV2 check when the new pki login is released
  const isPkiLoginV2 = useMemo(() => {
    return localStorage.getItem('pkiLogin') === 'v2';
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [formError, setFormError] = useState<AxiosError | undefined>(undefined);
  const [registrationData, setRegistrationData] = useState<{
    email: string;
    password: string;
    isResident: boolean | undefined;
    fullNameEn: string;
    fullNameAr: string;
    mobileCountryCode: FlagCountryCode;
    mobileNumber: string;
    countryCodeNumeric: string;
  }>({
    email: '',
    password: '',
    isResident: undefined,
    fullNameEn: '',
    fullNameAr: '',
    mobileCountryCode: 'OM' as FlagCountryCode,
    mobileNumber: '',
    countryCodeNumeric: '',
  });

  const { data: countries = [], isLoading: isCountriesLoading } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountries,
  });

  const { mutate: registerUser, isPending: isRegistrationLoading } = useMutation({
    mutationFn: async () => {
      const mobileCountryCode = COUNTRY_MOBILE_CODES.find(
        (countryMobileCode) => countryMobileCode.countryIso2 === registrationData.mobileCountryCode,
      );

      const [, countryCode] = registrationData.countryCodeNumeric.split('_');

      if (mobileCountryCode) {
        return registerAccount({
          email: registrationData.email,
          password: registrationData.password,
          fullNameEn: registrationData.fullNameEn,
          fullNameAr: registrationData.fullNameAr,
          phoneNumber: mobileCountryCode.mobileCode + registrationData.mobileNumber,
          countryCode: countryCode,
          isResident: registrationData.isResident,
          mpkiRegistration: mpkId ?? false,
          ...(txId ? { transactionId: txId } : {}),
        });
      }

      return Promise.reject(new Error('Invalid mobile country code'));
    },
    onSuccess: () => {
      console.debug('[/signup]: Registration successful');
      toast({
        message: t('register.successMessage'),
        variant: 'success',
      });
      setFormError(undefined);
      setCurrentStep(4);
    },
    onError: (error: AxiosError) => {
      // Registration errors are shown by AuthForm; avoid logging request/redirect data.
      setFormError(error);
    },
  });

  const renderStep = () => {
    // TODO: Remove the isPkiLoginV2 check when the new pki login is released
    if ((isPkiLoginV2 && currentStep === 2) || (currentStep === 1 && !isPkiLoginV2)) {
      return (
        <EmailPasswordForm
          data={{
            email: registrationData.email,
            password: registrationData.password,
          }}
          onComplete={({ email, password }) => {
            setRegistrationData((prev) => ({ ...prev, email, password }));
            setCurrentStep(isPkiLoginV2 ? 3 : 2);
          }}
        />
      );
      // TODO: Remove the isPkiLoginV2 check when the new pki login is released
    } else if ((isPkiLoginV2 && currentStep === 1) || (currentStep === 2 && !isPkiLoginV2)) {
      return (
        <ResidencyStatusForm
          data={{ isResident: registrationData.isResident }}
          onComplete={({ isResident }) => {
            setRegistrationData((prev) => ({ ...prev, isResident }));
            setCurrentStep(isPkiLoginV2 ? 2 : 3);
          }}
        />
      );
    } else if (currentStep === 3) {
      return (
        <PersonalDetailsForm
          data={{
            fullNameEn: registrationData.fullNameEn,
            fullNameAr: registrationData.fullNameAr,
            mobileCountryCode: registrationData.mobileCountryCode,
            mobileNumber: registrationData.mobileNumber,
            countryCodeNumeric: registrationData.countryCodeNumeric,
          }}
          onComplete={(data) => {
            setRegistrationData((prev) => ({ ...prev, ...data }));
            registerUser();
          }}
          countries={countries}
          isCountriesLoading={isCountriesLoading}
        />
      );
    } else {
      return (
        <VerifyEmail
          email={registrationData.email}
          onError={setFormError}
          redirectUrl={redirectUrl ?? getPendingGupLanding()}
        />
      );
    }
  };

  return (
    <AuthForm
      error={formError}
      {...(currentStep === 1 || currentStep === 2 || currentStep === 3
        ? {
            onBack: () => {
              if (currentStep === 3) {
                setCurrentStep(2);
              } else if (currentStep === 2) {
                setCurrentStep(1);
              } else {
                window.history.back();
              }
            },
          }
        : {})}
    >
      {isRegistrationLoading ? (
        <SigningInCover title={t('register.loadingTitle')} subtitle={t('register.loadingSubtitle')} />
      ) : (
        <>
          <RegistrationStepper currentStep={currentStep} isPkiLoginV2={isPkiLoginV2} />

          {renderStep()}
          {formError && getPendingGupLanding() && (
            <Button
              variant="secondary"
              onClick={() => {
                const visit = getGupVisit();
                if (visit) returnToGup(visit);
                else {
                  clearGupVisit();
                  navigate({ to: '/' });
                }
              }}
            >
              {t('gup.leave')}
            </Button>
          )}
        </>
      )}
    </AuthForm>
  );
}
